// src/pages/notifications/AddNotificationPage.tsx
import CustomButton from "@components/common/Button";
import CodeEditor from "@components/common/CodeEditor";
import LoadingSpinner from "@components/common/LoadingSpinner";
import { notify } from "@components/common/Notification";
import { checkFormChanges } from "@lib/checkFormChanges";
import { filterByLabel } from "@lib/filterByLabel";
import { DropdownService, NotificationService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { createNotificationPayload } from "@/utils/notificationValidator";
import {
  CHANNEL_OPTIONS,
  EVENT_OPTIONS,
  NotificationFormValues,
} from "@/utils/types/notification";
import { PageHeader, ProColumns } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
} from "antd";
import { Plus, Save, Trash, ChevronDown, ChevronUp, Edit2 } from "lucide-react";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BaseTable } from "@components/BaseTable";
import NotificationRecipientModal from "@components/NotificationRecipientModal";
import { gridName } from "@/utils/constant/gridName";

const CHANNEL_TYPES = {
  EMAIL: "email",
  SMS: "sms",
  SYSTEM: "system",
  WHATSAPP: "whatsapp",
} as const;

interface ReferenceFieldOption {
  title?: string;
  label?: string;
  value?: string;
  type?: string;
}

const SEND_ALERT_TYPES = {
  NEW: "new",
  SAVE: "save",
  DAYS_AFTER: "days_after",
  DAYS_BEFORE: "days_before",
  VALUE_CHANGE: "value_change",
} as const;

const AddNotificationPage: React.FC = (): JSX.Element => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditMode = !!id;

  const [selectedChannel, setSelectedChannel] = useState<string>(
    CHANNEL_TYPES.EMAIL
  );
  const [selectedSchemaId, setSelectedSchemaId] = useState("");
  const [sendAlertOn, setSendAlertOn] = useState<string>(SEND_ALERT_TYPES.NEW);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<any>(null);
  const [showPrintSettings, setShowPrintSettings] = useState(false);

  const attachPrint = Form.useWatch("attach_print", form);

  const handleAddRecipient = () => {
    setEditingRecipient(null);
    setIsModalOpen(true);
  };

  const handleEditRecipient = (recipient: any, index: number) => {
    setEditingRecipient({ ...recipient, index });
    setIsModalOpen(true);
  };

  const handleDeleteRecipient = (index: number) => {
    const currentRecipients =
      form.getFieldValue("notification_recipients") ?? [];
    const newRecipients = [...currentRecipients];
    newRecipients.splice(index, 1);
    form.setFieldsValue({ notification_recipients: newRecipients });
  };

  const handleModalSubmit = (values: any) => {
    const currentRecipients =
      form.getFieldValue("notification_recipients") ?? [];
    const newRecipients = [...currentRecipients];
    if (editingRecipient) {
      newRecipients[editingRecipient.index] = values;
    } else {
      newRecipients.push(values);
    }
    form.setFieldsValue({ notification_recipients: newRecipients });
    setIsModalOpen(false);
  };

  const { data: notification } = useQuery({
    queryKey: ["notification", id],
    queryFn: () => NotificationService.getNotificationById(id ?? ""),
    enabled: !!id,
  });

  const { data: schemaOptions = [], isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  const { data: tenantEmailConfigsRaw = [], isLoading: emailConfigsLoading } =
    useQuery({
      queryKey: ["tenant-email-config"],
      queryFn: () => NotificationService.getTenantEmailConfigs(),
    });

  // Map to show both label and value if label exists, else just value
  const tenantEmailConfigs = React.useMemo(
    () =>
      (tenantEmailConfigsRaw ?? []).map(
        (opt: { value: string; label: string | null }) => ({
          value: opt.value,
          label: (
            <div className="flex flex-col leading-tight">
              {opt.label && (
                <span className="mb-0.5 truncate text-base">{opt.label}</span>
              )}
              <span className="break-all font-medium">{opt.value}</span>
            </div>
          ),
          "data-label": opt.label ?? opt.value,
        })
      ),
    [tenantEmailConfigsRaw]
  );

  const selectedSchema = Form.useWatch("schema_name", form);

  const { data: roleOptions = [] } = useQuery({
    queryKey: ["tenant-roles"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });
  const { data: printFormatOptions = [], isLoading: printFormatsLoading } =
    useQuery({
      queryKey: ["print-formats", selectedSchemaId],
      queryFn: () => NotificationService.getPrintFormats(selectedSchemaId),
      enabled: !!selectedSchemaId, // Only runs when we have an ID
    });

  const schemaName = Form.useWatch("schema_name", form);
  const sendAlertOnWatch = Form.useWatch("send_alert_on", form);

  const { data: schemaFieldOptions = [], isFetching: schemaFieldsLoading } =
    useQuery({
      queryKey: ["schema-fields", schemaName],
      queryFn: () => NotificationService.getReferenceEmail(schemaName),
      enabled: !!schemaName,
    });

  const dateTimeOnlyOptions = useMemo(() => {
    const norm = (s?: string) => (s ?? "").toLowerCase();
    return (schemaFieldOptions ?? []).filter((f: any) =>
      ["datetime", "date", "datepicker"].includes(norm(f.type))
    );
  }, [schemaFieldOptions]);

  const referenceFieldOptions = useMemo(() => {
    if (
      sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_AFTER ||
      sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_BEFORE
    ) {
      return dateTimeOnlyOptions;
    }
    if (sendAlertOnWatch === SEND_ALERT_TYPES.VALUE_CHANGE) {
      return schemaFieldOptions;
    }
    return [];
  }, [sendAlertOnWatch, dateTimeOnlyOptions, schemaFieldOptions]);
  useEffect(() => {
    if (selectedSchema && schemaOptions.length > 0) {
      const schema = schemaOptions.find(
        (val: any) => val.label === selectedSchema
      );
      if (schema) {
        setSelectedSchemaId(schema.value);
      }
    }
  }, [selectedSchema, schemaOptions]);
  useEffect(() => {
    if (isEditMode && notification) {
      const apiResponse = notification as any;

      const channelMap: Record<string, string> = {
        Email: CHANNEL_TYPES.EMAIL,
        SMS: CHANNEL_TYPES.SMS,
        "System Notification": CHANNEL_TYPES.SYSTEM,
        WhatsApp: CHANNEL_TYPES.WHATSAPP,
        "1": CHANNEL_TYPES.EMAIL,
        "2": CHANNEL_TYPES.SMS,
        "3": CHANNEL_TYPES.SYSTEM,
        "4": CHANNEL_TYPES.WHATSAPP,
      };

      const alertMap: Record<string, string> = {
        New: SEND_ALERT_TYPES.NEW,
        Save: SEND_ALERT_TYPES.SAVE,
        "Days After": SEND_ALERT_TYPES.DAYS_AFTER,
        "Days Before": SEND_ALERT_TYPES.DAYS_BEFORE,
        "Value Change": SEND_ALERT_TYPES.VALUE_CHANGE,
        "1": SEND_ALERT_TYPES.NEW,
        "2": SEND_ALERT_TYPES.SAVE,
        "3": SEND_ALERT_TYPES.DAYS_AFTER,
        "4": SEND_ALERT_TYPES.DAYS_BEFORE,
        "5": SEND_ALERT_TYPES.VALUE_CHANGE,
      };

      const reference_field =
        apiResponse.reference_field ?? apiResponse.reference_date;
      const formData = {
        name: apiResponse.name,
        subject: apiResponse.subject,
        channel: channelMap[apiResponse.channel] ?? CHANNEL_TYPES.EMAIL,
        send_alert_on:
          alertMap[apiResponse.send_alert_on] ?? SEND_ALERT_TYPES.NEW,
        schema_name: apiResponse.document_type ?? apiResponse.schema_name,
        schema_id: apiResponse.schema_id, // Set schema_id here
        sender_email: apiResponse.sender ?? apiResponse.sender_email,
        condition: apiResponse.condition,
        message: apiResponse.message,
        attach_print: apiResponse.attach_print,
        print_format: apiResponse.print_format,
        reference_field: reference_field,
        days_before_or_after: apiResponse.days_before_or_after,
        notification_recipients: apiResponse.notification_recipients ?? [],
      };

      form.setFieldsValue(formData);
      setSelectedChannel(formData.channel);
      setSendAlertOn(formData.send_alert_on);
    } else {
      form.setFieldsValue({
        notification_recipients: [],
      });
    }
  }, [notification, isEditMode, form]);

  const handleChannelChange = (value: string) => {
    setSelectedChannel(value);
    if (value !== CHANNEL_TYPES.EMAIL) {
      form.setFieldsValue({
        subject: undefined,
        sender_email: undefined,
      });
    }
  };

  const handleSendAlertOnChange = (value: string) => {
    setSendAlertOn(value);

    if (
      value === SEND_ALERT_TYPES.DAYS_AFTER ||
      value === SEND_ALERT_TYPES.DAYS_BEFORE
    ) {
      form.setFieldsValue({ reference_field: undefined });
    } else if (value === SEND_ALERT_TYPES.VALUE_CHANGE) {
      form.setFieldsValue({
        days_before_or_after: undefined,
        reference_field: undefined,
      });
    } else {
      form.setFieldsValue({
        reference_field: undefined,
        days_before_or_after: undefined,
      });
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const values: NotificationFormValues = await form.validateFields();
      const payload = createNotificationPayload(values);

      if (isEditMode && id) {
        if (notification && !checkFormChanges(payload, notification, notify)) {
          setIsSubmitting(false);
          return;
        }
        await NotificationService.updateNotification(id, payload);
        notify.success("Notification updated successfully!");
      } else {
        await NotificationService.createNotification(payload);
      }

      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      navigate(navigationRoutes.Notification);
    } catch (_error) {
      // Validation failed, stop loading
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
  };

  if (isEditMode && !notification) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const columns: ProColumns<any>[] = [
    {
      title: "Document Field",
      dataIndex: "name",
    },
    {
      title: "Role",
      dataIndex: "receiver_by_role",
    },
    {
      title: "Condition",
      dataIndex: "condition",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEditRecipient(record, record.index)}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit Recipient"
        />,
        <Popconfirm
          key="delete"
          title={`Delete recipient "${record.name}"?`}
          onConfirm={() => handleDeleteRecipient(record.index)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Recipient"
          />
        </Popconfirm>,
      ],
    },
  ];

  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => handleAddRecipient()}
    >
      Add Recipient
    </CustomButton>,
  ];

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Notification" : "Add Notification"}
          onBack={() => window.history.back()}
          extra={[
            <CustomButton
              key="save"
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode
                  ? "Save Notification Changes"
                  : "Save New Notification"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>,
          ]}
        />
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: "Please enter notification name" },
              ]}
            >
              <Input placeholder="Enter notification name" />
            </Form.Item>
          </Col>

          {selectedChannel === CHANNEL_TYPES.EMAIL && (
            <Col span={12}>
              <Form.Item
                name="subject"
                label="Subject"
                rules={[{ required: true, message: "Please enter subject" }]}
              >
                <Input placeholder="Enter subject (can use {{ doc.field }} for dynamic content)" />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="channel"
              label="Channel"
              rules={[{ required: true, message: "Please select channel" }]}
            >
              <Select
                placeholder="Select channel"
                onChange={handleChannelChange}
                options={CHANNEL_OPTIONS}
              />
            </Form.Item>
          </Col>

          {selectedChannel && (
            <Col span={12}>
              <Form.Item
                name="schema_name"
                label="Schema Name"
                rules={[
                  { required: true, message: "Please enter schema name" },
                ]}
              >
                <Select
                  placeholder="Select schema"
                  showSearch
                  filterOption={filterByLabel}
                  disabled={isEditMode}
                  options={schemaOptions}
                  loading={schemaLoading}
                  onChange={(value, option) => {
                    form.setFieldsValue({
                      schema_name: option?.label as string,
                      schema_id: value,
                      reference_field: undefined,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          {selectedChannel === CHANNEL_TYPES.EMAIL && (
            <Col span={12}>
              <Form.Item
                name="sender_email"
                label="Sender Email"
                rules={[
                  { required: true, message: "Please select sender email" },
                ]}
              >
                <Select
                  placeholder="Select sender email"
                  options={tenantEmailConfigs}
                  loading={emailConfigsLoading}
                  showSearch
                  filterOption={filterByLabel}
                  optionLabelProp="data-label"
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          {selectedChannel && (
            <Col span={12}>
              <Form.Item
                name="send_alert_on"
                label="Send Alert On"
                rules={[
                  {
                    required: true,
                    message: "Please select when to send alert",
                  },
                ]}
              >
                <Select
                  placeholder="Select event"
                  value={sendAlertOn}
                  onChange={handleSendAlertOnChange}
                  options={EVENT_OPTIONS}
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          {(sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_AFTER ||
            sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_BEFORE ||
            sendAlertOnWatch === SEND_ALERT_TYPES.VALUE_CHANGE) && (
            <>
              <Col span={12}>
                <Form.Item
                  name="reference_field"
                  label="Reference Field"
                  rules={[
                    {
                      required: true,
                      message: "Please select reference field",
                    },
                  ]}
                >
                  <Select
                    placeholder={
                      sendAlertOnWatch === SEND_ALERT_TYPES.VALUE_CHANGE
                        ? "Select reference field"
                        : "Select reference date field"
                    }
                    options={(sendAlertOnWatch === SEND_ALERT_TYPES.VALUE_CHANGE
                      ? referenceFieldOptions
                      : referenceFieldOptions.filter(
                          (opt: ReferenceFieldOption) => {
                            const t = (opt.type ?? "").toLowerCase();
                            return ["datetime", "datepicker", "date"].includes(
                              t
                            );
                          }
                        )
                    ).map((opt: ReferenceFieldOption) => {
                      const displayLabel =
                        opt.title ?? opt.label ?? opt.value ?? "";
                      return {
                        value: opt.value ?? "",
                        label: displayLabel,
                        "data-label": displayLabel,
                        type: opt.type,
                      };
                    })}
                    optionLabelProp="data-label"
                    optionFilterProp="data-label"
                    showSearch
                    loading={schemaFieldsLoading}
                    filterOption={filterByLabel}
                    allowClear
                  />
                </Form.Item>
              </Col>

              {(sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_AFTER ||
                sendAlertOnWatch === SEND_ALERT_TYPES.DAYS_BEFORE) && (
                <Col span={12}>
                  <Form.Item
                    name="days_before_or_after"
                    label="Days Before/After"
                    rules={[
                      {
                        required: true,
                        message: "Please enter number of days",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      placeholder="Enter number of days"
                    />
                  </Form.Item>
                </Col>
              )}
            </>
          )}
        </Row>

        <Form.Item name="condition" label="Condition">
          <CodeEditor
            value={form.getFieldValue("condition") ?? ""}
            handleChange={val => form.setFieldsValue({ condition: val })}
            language="javascript"
          />
        </Form.Item>
        <Form.Item
          name="message"
          label="Message (HTML Template)"
          rules={[
            {
              required: true,
              message: "Please enter message (HTML Template)",
            },
          ]}
        >
          <CodeEditor
            value={form.getFieldValue("message") ?? ""}
            handleChange={val => form.setFieldsValue({ message: val })}
            language="html"
          />
        </Form.Item>

        <div className="border-b border-t border-gray-200 py-4">
          <div
            className="flex cursor-pointer items-center"
            onClick={() => setShowPrintSettings(!showPrintSettings)}
          >
            <h3 className="mr-2 text-lg font-semibold">Print Settings</h3>
            {showPrintSettings ? (
              <ChevronUp size={20} />
            ) : (
              <ChevronDown size={20} />
            )}
          </div>
          {showPrintSettings && (
            <div className="mt-4">
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="attach_print" valuePropName="checked">
                    <Checkbox>Attach Print</Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              {attachPrint && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="print_format" label="Print Format">
                      <Select
                        placeholder="Select print format"
                        options={printFormatOptions}
                        loading={printFormatsLoading}
                        showSearch
                        filterOption={filterByLabel}
                        value={printFormatOptions.values}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </div>
          )}
        </div>

        <div className="mt-4">
          <BaseTable<any>
            loading={false}
            columns={columns}
            dataSource={form.getFieldValue("notification_recipients") ?? []}
            page={1}
            pageSize={10}
            total={form.getFieldValue("notification_recipients")?.length}
            onPageChange={(page: number, pageSize: number) => {
              form.setFieldsValue({
                notification_recipients: form
                  .getFieldValue("notification_recipients")
                  ?.slice((page - 1) * pageSize, page * pageSize),
              });
              // navigate(`${navigationRoutes.NOTIFICATION_ADD}?${searchParams.toString()}`);
            }}
            toolBarRender={toolBarRender}
            rowKey="id"
            headerTitle="Recipients"
            request={() => Promise.resolve({})}
            tableName={gridName.notificationRecipientTable}
          />
        </div>

        <NotificationRecipientModal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
          initialData={editingRecipient}
          isEditMode={!!editingRecipient}
          roleOptions={roleOptions}
          confirmLoading={isSubmitting}
        />
      </Form>
    </Card>
  );
};

export default AddNotificationPage;
